import { useState, useEffect } from "react";
import { UseSliderProps, SliderState } from "./types";
import { DEFAULT_VALUES } from "./constants";
import { AnimationState } from "./enums";

export const useSlider = ({
  numberOfSlides,
  durationInMS = 3000,
  isPlayingOnInit,
}: UseSliderProps) => {
  const [state, setState] = useState<SliderState>(
    isPlayingOnInit
      ? {
          ...DEFAULT_VALUES,
          animationState: AnimationState.PLAYING,
          timeLeft: durationInMS,
        }
      : { ...DEFAULT_VALUES, timeLeft: durationInMS },
  );

  useEffect(() => {
    if (state.animationState !== AnimationState.PLAYING) return;

    const timer = setTimeout(() => {
      setState({
        ...state,
        activeSlide:
          state.activeSlide < numberOfSlides - 1 ? state.activeSlide + 1 : 0,
        timerStartedAt: Date.now(),
        timeLeft: durationInMS,
      });
    }, state.timeLeft);

    return () => clearTimeout(timer);
  }, [state.activeSlide, state.animationState, setState]);

  useEffect(() => {
    if (isPlayingOnInit && state.animationState !== AnimationState.PLAYING) {
      setState({
        ...state,
        animationState: AnimationState.PLAYING,
        timerStartedAt: Date.now(),
      });
    }
  }, []);

  const onChangeSlide = (slideIndex: number) => {
    if (slideIndex > numberOfSlides - 1 || slideIndex === state.activeSlide)
      return;

    setState({
      ...state,
      activeSlide: slideIndex,
      timerStartedAt: Date.now(),
      timeLeft: durationInMS,
    });
  };

  const onStartPlay = () =>
    setState({
      ...state,
      animationState: AnimationState.PLAYING,
      timerStartedAt: Date.now(),
    });
  const onStopPlay = () =>
    setState({ ...state, animationState: AnimationState.STOPED });
  const onPause = () =>
    setState({
      ...state,
      animationState: AnimationState.PAUSED,
      timeLeft: state.timeLeft - (Date.now() - state.timerStartedAt),
    });

  const onNextSlideSwitch = () =>
    setState({
      ...state,
      activeSlide:
        state.activeSlide < numberOfSlides - 1 ? state.activeSlide + 1 : 0,
      timerStartedAt: Date.now(),
      timeLeft: durationInMS,
    });
  const onPreviousSlideSwitch = () =>
    setState({
      ...state,
      activeSlide:
        state.activeSlide > 0 ? state.activeSlide - 1 : numberOfSlides - 1,
      timerStartedAt: Date.now(),
      timeLeft: durationInMS,
    });

  return {
    ...state,
    onChangeSlide,
    onStartPlay,
    onStopPlay,
    onPause,
    onNextSlideSwitch,
    onPreviousSlideSwitch,
  };
};
